import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import Icon, { IconName } from './icons/Icon';
import './InputSelect.css';

type InputSelectVariant = 'default' | 'ghost';

export interface SelectOption {
  value: string;
  label: string;
}

interface InputSelectProps {
  variant?: InputSelectVariant;
  hasLeftIcon?: boolean;
  leftIcon?: IconName;
  placeholder?: string;
  options: SelectOption[];
  value?: string | undefined;
  defaultValue?: string | undefined;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  /** 値が変わったときのシンプルな通知（combobox用） */
  onValueChange?: (newValue: string) => void;
  /** 存在しない入力文字列を新規追加するときに呼ばれる。呼び出し側で保存し、作成した option を返す */
  onCreateOption?: (label: string) => Promise<SelectOption> | SelectOption;
  onFocus?: (event: React.FocusEvent<HTMLSelectElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLSelectElement>) => void;
  disabled?: boolean;
  error?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
  /** 動作モード。既定は通常のselect。 */
  mode?: 'select' | 'combobox';
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

const InputSelect = forwardRef<HTMLSelectElement, InputSelectProps>((props, ref) => {
  const {
    variant = 'default',
    hasLeftIcon = false,
    leftIcon = 'chevronDown',
    placeholder,
    options,
    value,
    defaultValue,
    onChange,
    onValueChange,
    onCreateOption,
    onFocus,
    onBlur,
    disabled = false,
    error = false,
    required = false,
    name,
    id,
    mode = 'select',
    className = '',
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
  } = props;
  const hasEmptyOption = options.some(o => o.value === '');

  // === combobox mode state ===
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selectedLabel = useMemo(() => options.find(o => o.value === (value ?? ''))?.label ?? '', [options, value]);
  const [inputValue, setInputValue] = useState<string>(selectedLabel);
  useEffect(() => { setInputValue(selectedLabel); }, [selectedLabel]);

  const normalizedQuery = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!normalizedQuery) return options;
    return options.filter(o => o.label.toLowerCase().includes(normalizedQuery));
  }, [options, normalizedQuery]);

  const exactMatchExists = useMemo(() => {
    if (!normalizedQuery) return false;
    return options.some(o => o.label.trim().toLowerCase() === normalizedQuery);
  }, [options, normalizedQuery]);

  const closeDropdownSoon = () => {
    // クリック選択を優先させるため、若干遅らせて閉じる
    setTimeout(() => setOpen(false), 100);
  };

  const handleSelectValue = (newValue: string) => {
    onValueChange?.(newValue);
    // onChange もサポート（互換）
    if (onChange) {
      const evt = { target: { value: newValue } } as unknown as React.ChangeEvent<HTMLSelectElement>;
      onChange(evt);
    }
  };

  if (mode === 'combobox') {
    return (
      <div className={`input-select input-select--${variant} ${error ? 'input-select--error' : ''} ${disabled ? 'input-select--disabled' : ''} ${className}`}>
        <div className="input-select__state-layer">
          <div className="input-select__container input-select__container--combobox">
            {hasLeftIcon && (
              <div className="input-select__icon">
                <Icon name={leftIcon} size={24} />
              </div>
            )}
            <input
              ref={(node) => {
                inputRef.current = node;
                if (!ref) return;
                if (typeof ref === 'function') {
                  ref((node as unknown) as HTMLSelectElement);
                } else {
                  (ref as React.MutableRefObject<HTMLSelectElement | null>).current = (node as unknown) as HTMLSelectElement;
                }
              }}
              className="input-select__input text-body-medium"
              placeholder={placeholder}
              value={inputValue}
              onChange={(e) => { setInputValue(e.target.value); setQuery(e.target.value); setOpen(true); }}
              onFocus={() => { setOpen(true); setQuery(''); }}
              onBlur={() => { 
                // 入力が既存ラベルでなければ選択中表示に戻す
                if (inputRef.current && !options.some(o => o.label === inputRef.current!.value)) {
                  setInputValue(selectedLabel);
                }
                closeDropdownSoon(); 
              }}
              disabled={disabled}
              aria-label={ariaLabel}
              aria-describedby={ariaDescribedBy}
              aria-invalid={error}
              name={name}
              id={id}
              autoComplete="off"
            />
            <div className="input-select__chevron">
              <Icon name="chevronDown" size={20} />
            </div>
          </div>
          {open && (
            <div className="input-select__dropdown">
              {filtered.map((opt) => (
                <div
                  key={opt.value}
                  className={`input-select__option ${opt.value === value ? 'input-select__option--active' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { handleSelectValue(opt.value); setInputValue(opt.label); setOpen(false); }}
                >
                  {opt.label}
                </div>
              ))}
              {onCreateOption && normalizedQuery && !exactMatchExists && (
                <div
                  className="input-select__option input-select__option--add"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={async () => {
                    const created = await onCreateOption(inputRef.current?.value ?? '');
                    // 呼び出し元で options に反映されることを想定。ここでは選択状態にする
                    handleSelectValue(created.value);
                    setInputValue(created.label);
                    setOpen(false);
                  }}
                >
                  + {inputRef.current?.value ?? ''} を追加
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`input-select input-select--${variant} ${error ? 'input-select--error' : ''} ${disabled ? 'input-select--disabled' : ''} ${className}`}>
      <div className="input-select__state-layer">
        <div className="input-select__container">
          {hasLeftIcon && (
            <div className="input-select__icon">
              <Icon name={leftIcon} size={24} />
            </div>
          )}
          <select
            ref={ref}
            className="input-select__select text-body-medium"
            value={value}
            defaultValue={defaultValue}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            disabled={disabled}
            required={required}
            name={name}
            id={id}
            aria-label={ariaLabel}
            aria-describedby={ariaDescribedBy}
            aria-invalid={error}
          >
            {placeholder && !hasEmptyOption && (
              <option value="" disabled hidden>{placeholder}</option>
            )}
            {options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <div className="input-select__chevron">
            <Icon name="chevronDown" size={20} />
          </div>
        </div>
      </div>
    </div>
  );
});

InputSelect.displayName = 'InputSelect';

export default InputSelect;
